import { flatMap, isEmpty, uniq } from 'es-toolkit/compat';
import { parse } from '@tock/query-string';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import React, { useEffect } from 'react';

import { ACTION_JOIN_WAITLIST } from 'utils/analytics/eventTypeConstantsAmplitude';
import { ANY_EXPERIENCE_OPTION } from 'components/business/waitlist-dialog/WaitlistPreferencesForm';
import { DateTimeFormatter, LocalDate, ZoneId } from '@js-joda/core';
import { Dialog } from 'omakase';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import {
  WaitlistRequestFields,
  WaitlistRequestForm,
} from 'components/business/waitlist-dialog/WaitlistRequestForm';
import { WaitlistRequestStatus } from 'routes/business/search/ExperienceSearchRoute';
import { adjustDateForCutOff } from 'utils/constants';
import { core, toJSON } from '@tock/coretypes';
import { getAppPatron, getBusinessConfigSelector } from 'selectors/appSelector';
import { offeringsSelector } from 'selectors/calendarSelectors';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';

export interface WaitlistLocationState {
  status?: WaitlistRequestStatus;
  nearestAvailabilityMinutes?: number;
}

interface Props {
  experienceId?: number;
  backUrl?: string;
}

const WaitlistRoute: React.FC<Props> = ({ backUrl, experienceId }) => {
  const business = useSelector(getBusinessConfigSelector);
  const calendar = useSelector((state: StoreType) => state.calendar.calendar);
  const offerings = useSelector(offeringsSelector);
  const patron = useSelector(getAppPatron);
  const { actions } = useActions();
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const history = useHistory();
  const location = useLocation<{
    status?: WaitlistRequestStatus;
    nearestAvailabilityMinutes?: number;
  }>();

  const handleClose = () => {
    if (BROWSER && window.RunningInWidget) {
      window.parent.postMessage({ source: 'tock', action: 'close' }, '*');
    } else if (backUrl) {
      history.replace(backUrl);
    } else {
      history.replace('/');
    }
  };

  const handleSubmit = async (values: WaitlistRequestFields) => {
    const {
      desiredDates,
      desiredStartTime,
      desiredEndTime,
      desiredExperience,
      desiredPartySize,
      ...patron
    } = values;

    const desiredDateTimeRange = desiredDates.map((date) =>
      formatStringToInstant(date.toString(), desiredStartTime, desiredEndTime)
    );

    const ticketTypeId =
      desiredExperience === ANY_EXPERIENCE_OPTION ? undefined : Number(desiredExperience);

    const requestData: core.IWaitlist = {
      desiredSize: Number(desiredPartySize),
      desiredDateTimeRange,
      ticketTypeId,
      ...patron,
    };

    try {
      const payload = await addToWaitlist(requestData);
      return payload;
    } catch {
      return undefined;
    }
  };

  const formatStringToInstant = (date: string, startTime: string, endTime: string) => {
    const startDateTime = adjustDateForCutOff(date, startTime);
    const endDateTime = adjustDateForCutOff(date, endTime);
    const zoneId = ZoneId.of(business?.timeZone as string);

    return {
      startDateTime: startDateTime.atZone(zoneId).format(DateTimeFormatter.ISO_LOCAL_DATE_TIME),
      endDateTime: endDateTime.atZone(zoneId).format(DateTimeFormatter.ISO_LOCAL_DATE_TIME),
    };
  };

  const addToWaitlist = async (request: core.IWaitlist) => {
    // @lframnes - blindly copied this guest logic when separating functions
    // no idea if this is needed what putting this data in redux does...
    const guest = {
      firstName: request.firstName,
      lastName: request.lastName,
      email: request.email,
      phone: request.phone,
    };

    actions.lastGuest.setLastGuest(guest);

    const { search, state } = location;
    const { fromPage } = parse(search);
    const waitlistStatus = state?.status;
    const waitlistNearestAvailabilityMinutes = state?.nearestAvailabilityMinutes;
    let waitlistSource: string;

    switch (fromPage) {
      case 'experience':
        waitlistSource = 'Experience-level search';
        break;
      case 'event_detail':
        waitlistSource = 'Event detail page';
        break;
      default:
        waitlistSource = 'Business-level search';
        break;
    }

    amplitudeSendEvent(ACTION_JOIN_WAITLIST, {
      waitlistSource,
      waitlistStatus,
      waitlistNearestAvailabilityMinutes,
    });

    request.phone = await maybeGetBeautifiedPhone(request);

    try {
      const { payload } = await actions.waitlist.addToWaitlist(request, false);
      return toJSON<core.Waitlist>(payload);
    } catch {
      return;
    }
  };

  const maybeGetBeautifiedPhone = async ({ phone, phoneCountryCode }: core.IWaitlist) => {
    try {
      const { payload } = await actions.patron.validatePhoneRequest({
        phone: phone ?? '',
        phoneCountryCode: phoneCountryCode ?? '',
      });

      return payload.isValid ? payload.beautifiedPhone : phone;
    } catch {
      return phone;
    }
  };

  const { date: dateFromQuery, size: sizeFromProps, time } = parse(location.search);

  // A venue with no available party sizes has no bookable experiences. Rendering
  // the waitlist form in that state drives an empty-party-size update loop that
  // freezes the page, so notify the guest and close instead of showing the form.
  const availablePartySizes = uniq(
    flatMap(calendar?.ticketType ?? [], (ticketType) => ticketType.purchaseSize ?? [])
  );
  const hasNoBookableExperiences = !!calendar && isEmpty(availablePartySizes);

  useEffect(() => {
    if (hasNoBookableExperiences) {
      actions.toast.showError('Notify is not available.');
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNoBookableExperiences]);

  if (!offerings || !calendar || !business || hasNoBookableExperiences) {
    return null;
  }

  const initialRequest = {
    desiredPartySize: sizeFromProps,
    desiredDates: dateFromQuery ? [LocalDate.parse(dateFromQuery)] : [],
    desiredExperience: experienceId?.toString(),
    desiredStartTime: time,
    firstName: patron?.firstName,
    lastName: patron?.lastName,
    email: patron?.email,
    phone: patron?.phone,
    phoneCountryCode: patron?.phoneCountryCode,
    isoCountryCode: patron?.isoCountryCode,
  };

  return (
    <Dialog open={true} onClose={handleClose}>
      <WaitlistRequestForm
        onSubmit={handleSubmit}
        initialRequest={initialRequest}
        onCancel={handleClose}
      />
    </Dialog>
  );
};
export default WaitlistRoute;
